import React, { useEffect } from "react";
import { useRecoilState } from "recoil";
import { add } from "date-fns";
import { useForm } from "react-hook-form";
import { produce } from "immer";

import { MIN_DATES, MAX_DATES } from "config";
import t from "lib/i18n";
import { getDaySaveSearchURL } from "lib/searchUrl";
import useConfig from "hooks/useConfig";
import { daySaveFormState } from "state";
import useLayout from "hooks/useLayout";
import DateTime from "models/DateTime";
import "styles/pages/other.scss";
import useGTM, { useGTMChange } from "hooks/useGTM";
import * as tracking from "lib/tracking";

import Button, { ButtonTypes } from "components/ui/Button";
import NewDatesSelector from "components/datetime/DatesSelector";
import Passengers from "components/Passengers";
import StationsSelector from "components/StationsSelector";

const DaySave = () => {
  const {
    redirectUrl,
    searchCallBack,
    defaultValues,
    mode,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
  } = useConfig();

  const [form, setForm] = useRecoilState(daySaveFormState); // retrieve the form's state from a global state

  const configStationFrom = defaultValues?.ticketTypes?.daySave?.stationFrom;
  const configAdults = defaultValues?.ticketTypes?.daySave?.adults;
  const configChildren = defaultValues?.ticketTypes?.daySave?.children;
  const configFromDateTime = defaultValues?.ticketTypes?.daySave?.fromDateTime;

  const methods = useForm({
    defaultValues: form ?? {
      stationFrom: configStationFrom,
      adults: configAdults ?? 1,
      children: configChildren ?? 0,
      fromDateTime: configFromDateTime
        ? new DateTime(configFromDateTime)
        : new DateTime().addDays(3),
      ticketType: "daysave",
      toDateTime: null
    }
  });

  const { clearErrors, errors, handleSubmit, getValues, register, setValue, watch } = methods;
  const { isMobile } = useLayout(true);

  const { adults, children, fromDateTime, ticketType, toDateTime } = watch([
    "adults",
    "children",
    "fromDateTime",
    "stationFrom",
    "ticketType",
    "toDateTime"
  ]);

  useGTMChange(fromDateTime, tracking.trackDaySaveStartDate);
  useGTMChange(adults, tracking.trackDaySaveAdults);
  useGTMChange(children, tracking.trackDaySaveChildren);

  useEffect(() => {
    register({ name: "adults" }, { required: true });
    register({ name: "children" }, { required: true });
    register({ name: "stationFrom" }, { required: true });
    register({ name: "ticketType" }, { required: true });
    register({ name: "toDateTime" }, { required: false });
    register({ name: "fromDateTime" }, { required: true });

    return () => {
      // Save the form state
      const nextForm = produce(form, () => getValues);

      setForm(nextForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGTM = useGTM();
  const onSubmit = data => {
    const urlSearch = getDaySaveSearchURL({
      data,
      redirectUrl,
      mode,
      utmCampaign,
      utmContent,
      utmMedium,
      utmSource,
    });
    dispatchGTM(tracking.search());

    if (searchCallBack) {
      searchCallBack(urlSearch, data);
    } else {
      window.top.location.href = urlSearch;
    }
  };

  const setFieldValue = field => value => {
    setValue(field, value, true);

    if (field === "ticketType") {
      clearErrors("toDateTime");
    }
  };

  const onChangeFromDateTime = newValue => {
    setValue("fromDateTime", fromDateTime?.produce(newValue), true);
  };

  const onChangeToDateTime = newValue => {
    setValue("toDateTime", toDateTime?.produce(newValue), true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="otrl-jp__other__control-bar">
        <div className="otrl-jp__other__control-bar__stations">
          <StationsSelector
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            hideStationTo
            disableGeolocation
            disablePopularStations
            ticketType={ticketType}
            ticketSearchType="DaySaveOrigin"
          />
        </div>
        {isMobile ? (
          <div className="otrl-jp__other__journey-type-and-dates">
            <NewDatesSelector
              errors={errors}
              fromDateTime={fromDateTime}
              onChangeFromDateTime={onChangeFromDateTime}
              onChangeToDateTime={onChangeToDateTime}
              ticketType={ticketType}
              toDateTime={toDateTime}
              minDate={add(new Date(), MIN_DATES.DAY_SAVE)}
              maxDate={add(new Date(), MAX_DATES.DAY_SAVE)}
              isOther
            />
          </div>
        ) : (
          <NewDatesSelector
            errors={errors}
            fromDateTime={fromDateTime}
            onChangeFromDateTime={onChangeFromDateTime}
            onChangeToDateTime={onChangeToDateTime}
            ticketType={ticketType}
            toDateTime={toDateTime}
            minDate={add(new Date(), MIN_DATES.DAY_SAVE)}
            maxDate={add(new Date(), MAX_DATES.DAY_SAVE)}
            isOther
          />
        )}
        <Passengers
          adults={adults}
          children={children}
          onChange={setFieldValue}
          errorRailcards={errors["railcards"]}
          disableRailcards
        />
        <Button
          className="otrl-jp__other__submit"
          text={t(isMobile ? "tickets.submit.long" : "tickets.submit")}
          type={ButtonTypes.SUBMIT}
        />
      </div>
    </form>
  );
};

export default DaySave;
